'use client'

import React, { useRef, useState } from 'react'
import Image from 'next/image'
import { cn } from '@/utilities/ui'
import { CTAButton } from '@/components/ui/cta-button'
import { LOGO_BACKGROUNDS } from './constants'

export const LogoShowcase: React.FC<{ logoKitUrl: string }> = ({ logoKitUrl }) => {
  const [active, setActive] = useState(LOGO_BACKGROUNDS[0])

  // Roving focus + arrow-key selection across the background radios (WAI-ARIA radio pattern).
  const radioRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const onRadioKeyDown = (e: React.KeyboardEvent) => {
    const keys = LOGO_BACKGROUNDS.map((b) => b.key)
    const i = keys.indexOf(active.key)
    let next: number | null = null
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (i + 1) % keys.length
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (i - 1 + keys.length) % keys.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = keys.length - 1
    if (next === null) return
    e.preventDefault()
    const nextBg = LOGO_BACKGROUNDS[next]
    setActive(nextBg)
    radioRefs.current[nextBg.key]?.focus()
  }

  return (
    <div className="rounded-[16px] border border-civo-cloud p-4 sm:p-6">
      <div className="grid gap-4 sm:gap-6 md:grid-cols-[1fr_300px]">
        {/* Preview panel - background cross-fades, mark swaps for contrast */}
        <div
          className="relative flex min-h-[114px] items-center justify-center overflow-hidden rounded-[16px] transition-colors duration-500 md:min-h-[356px] motion-reduce:transition-none"
          style={{ backgroundColor: active.bg }}
        >
          {(['black', 'white'] as const).map((variant) => (
            <Image
              key={variant}
              src={`/assets-rebrand/logos/civo-${variant}.svg`}
              alt={variant === active.logoVariant ? 'Civo logo' : ''}
              aria-hidden={variant !== active.logoVariant}
              width={88}
              height={36}
              unoptimized
              className={cn(
                'absolute h-[64px] w-auto transition-opacity duration-500 sm:h-[80px] motion-reduce:transition-none',
                variant === active.logoVariant ? 'opacity-100' : 'opacity-0',
              )}
            />
          ))}
        </div>

        {/* Controls */}
        <div className="flex flex-col gap-2">
          <p className="body-small mb-1 text-black/60">Background</p>
          <div
            role="radiogroup"
            aria-label="Logo preview background"
            onKeyDown={onRadioKeyDown}
            className="flex flex-col gap-2"
          >
            {LOGO_BACKGROUNDS.map((bg) => {
              const selected = bg.key === active.key
              return (
                <button
                  key={bg.key}
                  ref={(el) => {
                    radioRefs.current[bg.key] = el
                  }}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActive(bg)}
                  className={cn(
                    'flex h-[52px] items-center gap-3 rounded-[8px] border bg-white px-3.5 text-left transition-all duration-200',
                    'hover:-translate-y-0.5 hover:shadow-[0_6px_16px_-10px_rgba(0,0,0,0.4)]',
                    'motion-reduce:transition-none motion-reduce:hover:translate-y-0',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-civo-black/40',
                    selected ? 'border-2 border-civo-black' : 'border border-civo-cloud',
                  )}
                >
                  <span
                    className={cn(
                      'size-7 shrink-0 rounded-[8px]',
                      bg.swatchBordered && 'border border-civo-cloud',
                    )}
                    style={{ backgroundColor: bg.bg }}
                  />
                  <span className="body-small flex-1 font-medium text-civo-black">{bg.label}</span>
                </button>
              )
            })}
          </div>

          <CTAButton
            variant="green"
            iconVariant="download"
            href={logoKitUrl}
            className="mt-2 w-full"
          >
            Download logo set
          </CTAButton>
        </div>
      </div>
    </div>
  )
}
